import apiClient from "../helpers/api-client";
import { sendLockdownAuthentication } from "../helpers/send-lockdown-authentication";
import { listenOpenerAuthInfo, openNewTabAndPostAuthInfo } from "../helpers/opener-authentication-info";
import { dispatchProxyMessage } from "@verity/extension/proxy-message/external";

export type Jwt = {
	sub?: string;
	uid?: string;
	iat?: number;
	exp?: number;
	jti?: string;
};

export interface PortalAuthenticationManager {
	init(): Promise<void>;

	openNewTab(url: URL): Promise<void>;

	fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
}

/**
 * Creates a new instance of the portal authentication manager.
 *
 * Note: This is just a wrapper to support legacy implementations,
 * internally, it uses the shared instance {@link apiClient} to make API calls.
 *
 * @returns A new instance of the portal authentication manager.
 */
export default function createPortalAuthenticationManager(): PortalAuthenticationManager {
	const setExtensionStorage = (request: string, key: string, value: string) => {
		if (window.chrome) {
			chrome.runtime.sendMessage(import.meta.env.VITE_EXTENSION_ID, { request: request, [key]: value }, (_response) => {
				if (chrome?.runtime?.lastError) {
					console.error(`Extension ${request} external message error: ${chrome.runtime.lastError.message}`);
				}
			});
		} else {
			dispatchProxyMessage({
				request: request,
				[key]: value,
			}).catch((e) => {
				console.error(`Extension ${request} external message error: ${JSON.stringify(e)}`);
			});
		}
	};

	const sendAuthenticationInfo = (newRefreshToken: string, newAccountId: string) => {
		try {
			setExtensionStorage("setRefreshToken", "refreshToken", newRefreshToken);
			setExtensionStorage("setAccountId", "accountId", newAccountId);
		} catch (e) {
			console.error("[PortalAuthenticationManager] Extension refresh token or account id external message failed.", e);
		}
		try {
			sendLockdownAuthentication({ refreshToken: newRefreshToken, accountId: newAccountId });
		} catch (e) {
			console.error("[PortalAuthenticationManager] Lockdown refresh token send failed.", e);
		}
	};

	const openNewTab = async (url: URL) => {
		const refreshToken = await apiClient.getRefreshToken();
		const accountId = await apiClient.getAccountId();

		const newUrl = new URL(url);
		newUrl.searchParams.delete("accessToken");
		newUrl.searchParams.delete("redirect");
		newUrl.searchParams.delete("extRefreshToken");
		if (accountId && refreshToken) {
			newUrl.searchParams.set("redirect", "newTab");
			try {
				await openNewTabAndPostAuthInfo({ refreshToken, accountId, url: newUrl });
			} catch (e) {
				console.error(e);
			}
		} else {
			window.open(newUrl);
		}
	};

	return {
		async init() {
			const redirectParam = new URL(window.location.href)?.searchParams?.get("redirect");
			if (redirectParam === "lti1p3") {
				await apiClient.updateTokens();
				return;
			}

			// TODO: remove closeParam when lms auth uses authorization code
			const closeParam = new URL(window.location.href)?.searchParams?.get("close");
			if (redirectParam === "newTab") {
				try {
					const authInfo = await listenOpenerAuthInfo();
					if (authInfo.refreshToken && authInfo.accountId) {
						await apiClient.setRefreshToken(authInfo.refreshToken);
						await apiClient.setAccountId(authInfo.accountId);
						await apiClient.updateTokens();
						await fetch(`/api/verity/session/end`, { credentials: "include" });
					}
					// TODO: remove once the lms auth uses the authorization code
					if (closeParam === "close") {
						await this.fetch(`/api/verity/session/validate/auth`);
						window.close();
					}
					return;
				} catch (e) {
					console.error(e);
				}
			}

			const refreshTokenParam = new URL(window.location.href)?.searchParams?.get("refreshToken");
			const extRefreshTokenParam = new URL(window.location.href)?.searchParams?.get("extRefreshToken");
			const accountIdParam = new URL(window.location.href)?.searchParams?.get("accountId");
			if (refreshTokenParam && accountIdParam) {
				if (extRefreshTokenParam) {
					sendAuthenticationInfo(extRefreshTokenParam, accountIdParam);
				}
				await apiClient.setRefreshToken(refreshTokenParam);
				await apiClient.setAccountId(accountIdParam);
				await apiClient.updateTokens();
				await fetch(`/api/verity/session/end`, { credentials: "include" });
			}
			// TODO: remove once the lms auth uses the authorization code
			if (closeParam === "close") {
				await this.fetch(`/api/verity/session/validate/auth`);
				window.close();
			}
		},
		openNewTab,
		async fetch(input, init) {
			return await apiClient.fetch(input, init);
		},
	};
}
